import { initializeEyeaiAvatarComponent } from './cdn-loader';

declare const __ASSETS_BASE_URL__: string;
declare const __SOCKET_URL__: string;
declare const __FILE_API_URL__: string;

async function runEyeaiAvatarComponent({
  apiKey, 
  autoInit, 
  buttonStartColor, 
  buttonStartContent,
  debug
}: {
  apiKey: string, 
  autoInit: boolean, 
  buttonStartColor: string, 
  buttonStartContent: string,
  debug: boolean
}): Promise<void> {
  const container = document.querySelector('body') as HTMLElement;

  if (container) {
    const eyeaiAvatarComponent: any = document.createElement('eyeai-avatar-component');

    eyeaiAvatarComponent.apiKey = apiKey;
    eyeaiAvatarComponent.assetsBaseUrl = __ASSETS_BASE_URL__;
    eyeaiAvatarComponent.fileApiUrl = __FILE_API_URL__;
    eyeaiAvatarComponent.socketUrl = __SOCKET_URL__;
    if (autoInit) {
      eyeaiAvatarComponent.autoInit = autoInit;
    }
    if (buttonStartColor) {
      eyeaiAvatarComponent.buttonStartColor = buttonStartColor;
    }
    if (buttonStartContent) {
      eyeaiAvatarComponent.buttonStartContent = buttonStartContent;
    }
    if (debug) {
      eyeaiAvatarComponent.debug = debug;
    }

    container.appendChild(eyeaiAvatarComponent);
  } else {
    console.error(`Element body not found in the DOM.`);
    throw new Error(`Element body not found in the DOM.`);
  }
}

function checkPropValues(settings: { 
  apiKey: string,
  autoInit: boolean,
  buttonStartColor: string,
  buttonStartContent: string,
  debug: boolean
}) {
  for (const [prop, value] of Object.entries(settings)) {
    switch (prop) {
      case 'apiKey':
        if (!value || typeof value !== 'string') {
          throw new Error(`${prop} is required and must be of type string`);
        }
        break;
      case 'autoInit':
      case 'debug':
        if (typeof value !== 'boolean') {
          throw new Error(`${prop} must be of type boolean`);
        }
        break;
      case 'buttonStartColor':
      case 'buttonStartContent':
        if (value && typeof value !== 'string') {
          throw new Error(`${prop} must be of type string`);
        }
        break;
      default:
        break;
    }
  }
}

/**
 * Loads and initializes the EyeAI Avatar component in the specified container.
 * This method is responsible for:
 * 1. Preloading CDN resources for the eyeai-avatar-component package
 * 2. Initializing the avatar with the provided configuration
 * 3. Setting up the container where the avatar will render
 * 4. Handling DOM ready state to ensure proper initialization
 * @param apiKey - Chatbot API key for authentication and config (required)
 * @param autoInit - Whether to auto-initialize on load
 * @param buttonStartColor - Color code for the start button
 * @param buttonStartContent - Text or URL for the start button content
 * @returns Promise that resolves to the avatar element on success, or rejects on error
 * @throws {Error} - Throws an error if:
 *                   - apiKey is invalid or empty
 *                   - CDN resources fail to load
 *                   - Avatar initialization fails
 */
export async function loadEyeaiAvatarComponent({
  apiKey, 
  autoInit = false,
  buttonStartColor = '',
  buttonStartContent = '',
  debug = false
} : {
  apiKey: string, 
  autoInit: boolean,
  buttonStartColor: string,
  buttonStartContent: string,
  debug: boolean
}): Promise<void> {
  try {
    const run = async () => {
      try {
        const settings = {
          apiKey,
          autoInit,
          buttonStartColor,
          buttonStartContent,
          debug
        }

        checkPropValues(settings);

        await initializeEyeaiAvatarComponent(__ASSETS_BASE_URL__);

        await runEyeaiAvatarComponent(settings);
      } catch (error) {
        throw error;
      }
    };

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      await run();
    } else {
      document.addEventListener('DOMContentLoaded', async () => {
        await run();
      });
    }
  } catch (error) {
    console.error('Error loading Fip Agent:', error);
    throw `Error loading Fip Agent: ${error}`;
  }
}

if (import.meta.url.includes('apiKey')) {
  const url = new URL(import.meta.url);
  const apiKey = url.searchParams.get('apiKey') || '';
  const autoInit = url.searchParams.get('autoInit') === 'true';
  const buttonStartColor = url.searchParams.get('buttonStartColor') || '';
  const buttonStartContent = url.searchParams.get('buttonStartContent') || '';
  const debug = url.searchParams.get('debug') === 'true';

  loadEyeaiAvatarComponent({
    apiKey, 
    autoInit,
    buttonStartColor,
    buttonStartContent,
    debug
    
  }).catch((error) => {
    console.error('Error loading EyeAI Avatar Component:', error?.message ?? error);
  });
}